import React, { useEffect, useState } from "react";

import CarouselCards from "../../Widgets/Carousels/CarouselCards";
import GoogleMapReact from "google-map-react";
import { chain } from "ramda";
import styles from "./GoogleMap.module.scss";
import { uniq } from "ramda";

const DEFAULT_ZOOM = 12;
const CAROUSEL_CARD_HEIGHT_OFFSET = 345;

const resultToPins = (result) =>
  [result, ...result.edges]
    .filter((x) => x.latlng)
    .map((x) => ({
      result,
      latlng: x.latlng,
      title: x.title,
    }));

const MapPin = ({
  onClick,
  onMouseEnter,
  onMouseLeave,
  isSelected,
  isHovering,
}) => (
  <div
    className={styles.pinWrapper}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{ zIndex: isSelected || isHovering ? 10 : 0 }}
  >
    <svg
      className={[
        styles.mapPin,
        isSelected ? styles.mapPinSelected : "",
        isHovering ? styles.mapPinHovering : "",
      ].join(" ")}
      viewBox="0 0 26 33"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M21.4321 11.75C21.4321 10.6009 21.2067 9.46312 20.7686 8.40152C20.3306 7.33992 19.6886 6.37533 18.8792 5.56282C18.0699 4.7503 17.109 4.10578 16.0515 3.66605C14.9941 3.22633 13.8607 3 12.7161 3C11.5714 3 10.438 3.22633 9.38056 3.66605C8.32308 4.10578 7.36223 4.7503 6.55287 5.56282C5.74351 6.37533 5.10149 7.33992 4.66347 8.40152C4.22545 9.46312 4 10.6009 4 11.75C4 13.4838 4.51051 15.0963 5.37589 16.4563H5.36593C8.30449 21.075 12.7161 28 12.7161 28L20.0662 16.4563H20.0575C20.9545 15.0517 21.4315 13.4184 21.4321 11.75ZM12.7161 15.5C11.7254 15.5 10.7752 15.1049 10.0747 14.4017C9.37416 13.6984 8.9806 12.7446 8.9806 11.75C8.9806 10.7554 9.37416 9.80161 10.0747 9.09835C10.7752 8.39509 11.7254 8 12.7161 8C13.7068 8 14.6569 8.39509 15.3574 9.09835C16.058 9.80161 16.4515 10.7554 16.4515 11.75C16.4515 12.7446 16.058 13.6984 15.3574 14.4017C14.6569 15.1049 13.7068 15.5 12.7161 15.5Z" />
    </svg>
  </div>
);

const fitPinsToMapView = ({
  showAllPins,
  allResultsPins,
  mapObject,
  googleMapsAPI,
  result,
}) => {
  if (!(mapObject && result && googleMapsAPI)) return;

  const pinsInView = showAllPins ? allResultsPins : resultToPins(result);

  if (!pinsInView.length) return;

  const lats = uniq(pinsInView.map(({ latlng }) => latlng.lat));
  const lngs = uniq(pinsInView.map(({ latlng }) => latlng.lng));

  mapObject.fitBounds(
    new googleMapsAPI.LatLngBounds(
      {
        // Southwest
        lat: Math.min(...lats),
        lng: Math.min(...lngs),
      },
      {
        // Northeast
        lat: Math.max(...lats),
        lng: Math.max(...lngs),
      }
    ),
    { bottom: CAROUSEL_CARD_HEIGHT_OFFSET } // Pad from below by carousel card size.
  );

  if (lats.length < 2 || lngs.length < 2) {
    // Calling the map object's `setZoom` directly since the `google-map-react` zoom variable is not respected, probably because of `fitBounds` above.
    mapObject.setZoom(DEFAULT_ZOOM);
    mapObject.panBy(0, Math.ceil(CAROUSEL_CARD_HEIGHT_OFFSET / 2));
  }
};

const GoogleMap = ({ results, sendEvent, state, onSuggestionClick }) => {
  const [showAllPins, setShowAllPins] = useState(true);
  const [selectedResultIndex, setSelectedResultIndex] = useState(0);
  const [hoveredResultIndex, setHoveredResultIndex] = useState(null);
  const [allResultsPins, setAllResultsPins] = useState([]);
  const [mapObject, setMapObject] = useState();
  const [googleMapsAPI, setGoogleMapsAPI] = useState();

  const selectResult = (resultIndex) => {
    setShowAllPins(false);
    setSelectedResultIndex(resultIndex);
  };

  useEffect(() => {
    setAllResultsPins(chain(resultToPins, results));
  }, [results]);

  const result = results && results[selectedResultIndex];
  useEffect(() => {
    fitPinsToMapView({
      showAllPins,
      allResultsPins,
      mapObject,
      googleMapsAPI,
      result,
    });
  }, [result, mapObject, googleMapsAPI, allResultsPins, showAllPins]);

  if (!allResultsPins.length) {
    return null;
  }
  return (
    <div
      className={[
        styles.googleMapWrapper,
        state ? styles.open : styles.close,
      ].join(" ")}
      style={{ height: "99%" }}
    >
      <GoogleMapReact
        bootstrapURLKeys={{ key: "AIzaSyA8EqfzGxUb55Seg0kGL9PtKARAq3sIL3E" }}
        defaultCenter={allResultsPins[0].latlng}
        defaultZoom={DEFAULT_ZOOM}
        options={() => ({
          streetViewControl: false,
          zoomControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false, // Disable clickable POIs, e.g. "East side".
          styles: [{ stylers: [{ lightness: -8 }] }],
        })}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => {
          setMapObject(map);
          setGoogleMapsAPI(maps);
        }}
        style={{
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        {allResultsPins.map((pin, index) => (
          <MapPin
            key={index}
            result={pin.result}
            lat={pin.latlng.lat}
            lng={pin.latlng.lng}
            isSelected={pin.result === results[selectedResultIndex]}
            isHovering={
              hoveredResultIndex !== null &&
              pin.result === results[hoveredResultIndex]
            }
            onMouseEnter={() =>
              setHoveredResultIndex(results.indexOf(pin.result))
            }
            onMouseLeave={() => setHoveredResultIndex(null)}
            onClick={() => setSelectedResultIndex(results.indexOf(pin.result))}
          />
        ))}
      </GoogleMapReact>
      <div
        className={[
          styles.carouselWrapper,
          results.length === 1 ? styles.carouselWrapperSingleResult : "",
        ].join(" ")}
      >
        <CarouselCards
          mapMode={true}
          onSuggestionClick={onSuggestionClick}
          selectedResultIndex={selectedResultIndex}
          hoveredResultIndex={hoveredResultIndex}
          onMapClick={(resultsFromCard) =>
            selectResult(results.indexOf(resultsFromCard[0]))
          }
          className={results.length > 1 ? styles.multipleResultsCarousel : ""}
          items={results}
          onClickItem={(_, index) => selectResult(index)}
          onHoverToggle={(index) => (isHovering) =>
            setHoveredResultIndex(isHovering ? index : null)}
          sendEvent={sendEvent}
        />
      </div>
    </div>
  );
};

export default GoogleMap;
